/**
 * Typed depletions-API client.
 *
 * Mirrors the FastAPI schemas in
 *   hooten-young-sales/src/hy_sales/schemas/depletions.py
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from './client';

/** Inclusive ISO date bounds every depletions endpoint accepts.
 *  Lived in `api/sales.ts` until the invoice-sales views were
 *  retired; it is a depletions concern now. */
export interface DateRange {
  from?: string;
  to?: string;
}

export interface DepletionsKpis {
  total_9l: string;
  total_physical: string;
  unique_accounts: number;
  unique_products: number;
  unique_states: number;
  unique_distributors: number;
  period_start: string | null;
  period_end: string | null;
  // Day-level cutoff inside period_end's month — period_end is always
  // first-of-month, so this is what says "August stops on the 23rd".
  // Null when that month is complete or no cutoff was recorded.
  data_through: string | null;
  avg_9l_per_account: string | null;
}

export function useDepletionsKpis(range?: DateRange): UseQueryResult<DepletionsKpis> {
  return useQuery({
    queryKey: ['depletions', 'kpis', range ?? {}],
    queryFn: () =>
      apiGet<DepletionsKpis>('sales', '/api/depletions/kpis', {
        from: range?.from,
        to: range?.to,
      }),
  });
}

export interface DepletionsTrendPoint {
  period: string;
  cases_9l: string;
  cases_physical: string;
  active_accounts: number;
}

export interface DepletionsTrendResponse {
  grain: string;
  points: DepletionsTrendPoint[];
}

export function useDepletionsTrend(
  range?: DateRange,
  grain: 'month' = 'month',
  productId?: number,
): UseQueryResult<DepletionsTrendResponse> {
  return useQuery({
    queryKey: ['depletions', 'trend', grain, range ?? {}, productId ?? null],
    queryFn: () =>
      apiGet<DepletionsTrendResponse>('sales', '/api/depletions/trend', {
        from: range?.from,
        to: range?.to,
        grain,
        product_id: productId,
      }),
  });
}

// MUST match `schemas/depletions.py::StateProductSeries`.
export interface StateProductSeries {
  product_id: number | null;
  product_name: string;
  cases_9l: string[];
}

// MUST match `schemas/depletions.py::StateProductTrendResponse`.
export interface StateProductTrendResponse {
  state_code: string;
  months: string[];
  products: StateProductSeries[];
}

export function useStateProductTrend(
  stateCode: string | null,
): UseQueryResult<StateProductTrendResponse> {
  return useQuery({
    queryKey: ['depletions', 'state-product-trend', stateCode],
    enabled: !!stateCode,
    queryFn: () =>
      apiGet<StateProductTrendResponse>('sales', '/api/depletions/state-product-trend', {
        state_code: stateCode,
      }),
  });
}

export interface DepletionsProductPerformance {
  product_id: number;
  product_name: string;
  cases_9l: string;
  cases_physical: string;
  account_count: number;
  state_count: number;
  pct_of_9l: number;
}

export interface DepletionsStatePerformance {
  state_code: string | null;
  cases_9l: string;
  cases_physical: string;
  account_count: number;
  pct_of_9l: number;
}

export interface DepletionsStateResponse {
  states: DepletionsStatePerformance[];
  total_9l: string;
}

export function useDepletionsByState(range?: DateRange): UseQueryResult<DepletionsStateResponse> {
  return useQuery({
    queryKey: ['depletions', 'by-state', range ?? {}],
    queryFn: () =>
      apiGet<DepletionsStateResponse>('sales', '/api/depletions/by-state', {
        from: range?.from,
        to: range?.to,
      }),
  });
}

export interface TopAccount {
  account_id: number;
  name: string;
  state_code: string | null;
  city: string | null;
  distributor_name: string | null;
  premises_type: string | null;
  // True for online-retailer accounts — flagged as an ONLINE channel.
  is_online_retailer: boolean;
  cases_9l: string;
  cases_physical: string;
  product_count: number;
  last_active_period: string | null;
  pct_of_9l: number;
}

export interface TopAccountsResponse {
  accounts: TopAccount[];
  total_9l: string;
}

export function useTopAccounts(range?: DateRange, limit = 20): UseQueryResult<TopAccountsResponse> {
  return useQuery({
    queryKey: ['depletions', 'top-accounts', range ?? {}, limit],
    queryFn: () =>
      apiGet<TopAccountsResponse>('sales', '/api/depletions/top-accounts', {
        from: range?.from,
        to: range?.to,
        limit,
      }),
  });
}

// ---- follow-up tracker ----

// MUST match `schemas/strategic.py::FollowUpMonthlyVolume`.
export interface FollowUpMonthlyVolume {
  period: string;
  cases_9l: string;
}

// MUST match `schemas/strategic.py::FollowUpAccount`.
export interface FollowUpAccount {
  account_id: number;
  name: string;
  address: string | null;
  state_code: string | null;
  city: string | null;
  county: string | null;
  zip_code: string | null;
  distributor_name: string | null;
  last_active: string;
  last_active_label: string;
  days_since: number;
  total_9l: string;
  product_count: number;
  products: string[];
  // Catalog SKUs this account has NOT bought — the full depletions
  // catalog minus `products`, ordered by catalog-wide volume (best-
  // selling gaps first). Rendered as "opportunity" chips in the
  // Missing Products column so reps can see what to pitch.
  missing_products: string[];
  customer_since: string | null;
  customer_since_label: string | null;
  recent_3m_9l: string;
  prior_3m_9l: string;
  velocity_pct: number | null;
  // 'ON' (bars, restaurants) / 'OFF' (liquor stores, retail) /
  // 'NA' (broker says not-applicable) / null (no data yet).
  premises_type: string | null;
  // True for online-retailer accounts — flagged as an ONLINE channel.
  is_online_retailer: boolean;
  monthly_series: FollowUpMonthlyVolume[];

  // Rep-context fields — populated only when the response comes from
  // the Field Sales endpoint.  See FollowUpAccountRepContext below.
  is_pinned?: boolean;
  last_visit_date?: string | null;
  last_outcome?: string | null;
  last_note_excerpt?: string | null;
  // When the latest note was saved (ISO timestamp) and by whom.
  last_logged_at?: string | null;
  last_logged_by?: string | null;
}

export interface FollowUpBucket {
  label: string;
  count: number;
  total_9l: string;
  accounts: FollowUpAccount[];
}

// Rep-context fields are populated only when the data comes from the
// Field Sales endpoint; the depletions endpoint leaves them at their
// defaults (false / null).  Declared here so the FollowUpTracker UI
// can read them when present without a separate Field-specific type.
export interface FollowUpAccountRepContext {
  is_pinned?: boolean;
  last_visit_date?: string | null;
  last_outcome?: string | null;
  last_note_excerpt?: string | null;
  // When the latest note was saved (ISO timestamp) and by whom.
  last_logged_at?: string | null;
  last_logged_by?: string | null;
}

export interface TerritorySummary {
  states: string[];
  total_accounts: number;
  with_shipment_history: number;
  without_shipment_history: number;
}

export interface DataWindow {
  earliest: string; // ISO date
  latest: string; // ISO date
  // Day-level cutoff inside `latest`'s month. `latest` is first-of-month
  // and so reads as a whole month even when the feed stopped partway in.
  data_through?: string | null;
}

export interface FollowUpTrackerResponse {
  reference_date: string;
  buckets: FollowUpBucket[];
  territory?: TerritorySummary | null;
  data_window?: DataWindow | null;
}

export function useFollowUpTracker(): UseQueryResult<FollowUpTrackerResponse> {
  return useQuery({
    queryKey: ['depletions', 'follow-ups'],
    queryFn: () => apiGet<FollowUpTrackerResponse>('sales', '/api/depletions/follow-ups'),
  });
}

// ---- new vs lost accounts ----

export interface AccountBrief {
  account_id: number;
  name: string;
  // Mailing address — included so exports can be printed / mapped.
  address: string | null;
  state_code: string | null;
  city: string | null;
  county: string | null;
  zip_code: string | null;
  distributor_name: string | null;
  premises_type: string | null;
  // True for online-retailer accounts — flagged as an ONLINE channel.
  is_online_retailer: boolean;
  cases_9l: string;
}

export interface NewVsLostAccountsResponse {
  reference_date: string;
  window_months: number;
  recent_window_start: string;
  prior_window_start: string;
  prior_window_end: string;
  new_count: number;
  lost_count: number;
  new_total_9l: string;
  lost_total_9l: string;
  new_accounts: AccountBrief[];
  lost_accounts: AccountBrief[];
}

export function useNewVsLostAccounts(windowMonths = 3): UseQueryResult<NewVsLostAccountsResponse> {
  return useQuery({
    queryKey: ['depletions', 'new-vs-lost', windowMonths],
    queryFn: () =>
      apiGet<NewVsLostAccountsResponse>('sales', '/api/depletions/new-vs-lost', {
        window_months: windowMonths,
      }),
  });
}

// ---- velocity analysis ----

export type VelocityCategory = 'accelerating' | 'steady' | 'declining' | 'new' | 'silent';

export interface VelocityAccount {
  account_id: number;
  name: string;
  state_code: string | null;
  city: string | null;
  distributor_name: string | null;
  months_active: number;
  total_9l: string;
  avg_9l_per_month: string;
  recent_3m_avg: string;
  prior_3m_avg: string;
  velocity_change_pct: number | null;
  category: VelocityCategory;
}

export interface VelocityCategoryStats {
  category: VelocityCategory;
  count: number;
  total_9l: string;
}

export interface VelocityAnalysisResponse {
  reference_date: string;
  accounts: VelocityAccount[];
  category_stats: VelocityCategoryStats[];
}

export function useVelocityAnalysis(): UseQueryResult<VelocityAnalysisResponse> {
  return useQuery({
    queryKey: ['depletions', 'velocity'],
    queryFn: () => apiGet<VelocityAnalysisResponse>('sales', '/api/depletions/velocity'),
  });
}

// ---- growth & decline ----

export interface GrowthDeclineAccount {
  account_id: number;
  name: string;
  // Mailing address — included so exports can be printed / mapped.
  address: string | null;
  state_code: string | null;
  city: string | null;
  county: string | null;
  zip_code: string | null;
  distributor_name: string | null;
  premises_type: string | null;
  // True for online-retailer accounts — flagged as an ONLINE channel.
  is_online_retailer: boolean;
  recent_9l: string;
  prior_9l: string;
  diff_9l: string;
}

export interface GrowthDeclineResponse {
  reference_date: string;
  window_months: number;
  recent_window_start: string;
  prior_window_start: string;
  prior_window_end: string;
  growing_count: number;
  declining_count: number;
  growing_volume: string;
  declining_volume: string;
  growing_accounts: GrowthDeclineAccount[];
  declining_accounts: GrowthDeclineAccount[];
}

// ---- account x month heatmap ----

export type DepletionsFrequency = 'Monthly' | 'Bi-Monthly' | 'Quarterly' | 'Infrequent';

// MUST match `schemas/depletions.py::AccountMonthlyProductBreakdown`.
export interface AccountMonthlyProductBreakdown {
  product_id: number;
  product_name: string;
  cases_9l: string;
}

export interface AccountMonthlyVolume {
  period: string;
  cases_9l: string;
  // Per-(account, month) product mix — sorted by cases_9l desc.
  // Empty array when the account didn't move any product that month.
  products: AccountMonthlyProductBreakdown[];
}

export interface AccountMonthlyGridRow {
  account_id: number;
  name: string;
  // Full mailing-address fields — surfaced in the heatmap's account
  // label hover tooltip so the rep has everything they need.
  address: string | null;
  state_code: string | null;
  city: string | null;
  county: string | null;
  zip_code: string | null;
  distributor_code: string | null;
  premises_type: string | null;
  // True for online-retailer accounts — flagged as an ONLINE channel.
  is_online_retailer: boolean;
  total_9l: string;
  months_active: number;
  frequency: DepletionsFrequency;
  monthly_volumes: AccountMonthlyVolume[];
}

export interface AccountMonthlyGridResponse {
  period_start: string | null;
  period_end: string | null;
  months: string[];
  accounts: AccountMonthlyGridRow[];
}

export function useAccountMonthlyGrid(
  limit = 20,
  range?: DateRange,
): UseQueryResult<AccountMonthlyGridResponse> {
  return useQuery({
    queryKey: ['depletions', 'monthly-grid', limit, range ?? {}],
    queryFn: () =>
      apiGet<AccountMonthlyGridResponse>('sales', '/api/depletions/account-monthly-grid', {
        limit,
        from: range?.from,
        to: range?.to,
      }),
  });
}

// ---- 2026 Orders (monthly depletion activity) ----

// MUST match `schemas/depletions.py::OrderProductLine`.
export interface OrderProductLine {
  name: string;
  cases_9l: string;
}

// MUST match `schemas/depletions.py::MonthlyDepletionOrder`.
export interface MonthlyDepletionOrder {
  account_id: number;
  name: string;
  // Mailing address — included so exports can be printed / mapped.
  address: string | null;
  state_code: string | null;
  city: string | null;
  county: string | null;
  zip_code: string | null;
  premises_type: string | null;
  is_online_retailer: boolean;
  period_month: string; // ISO date, first of the month
  cases_9l: string;
  cases_physical: string;
  product_count: number;
  products: OrderProductLine[];
}

// MUST match `schemas/depletions.py::MonthlyDepletionOrdersResponse`.
export interface MonthlyDepletionOrdersResponse {
  orders: MonthlyDepletionOrder[];
  total_count: number;
  date_from: string | null;
  date_to: string | null;
}

// Per-(account, month) depletion "orders" for a date window. We fetch a
// whole year at once and window/sort/search client-side.
export function useMonthlyDepletionOrders(
  range?: DateRange,
  limit = 10000,
): UseQueryResult<MonthlyDepletionOrdersResponse> {
  return useQuery({
    queryKey: ['depletions', 'monthly-orders', range ?? {}, limit],
    queryFn: () =>
      apiGet<MonthlyDepletionOrdersResponse>('sales', '/api/depletions/monthly-orders', {
        from: range?.from,
        to: range?.to,
        limit,
      }),
  });
}

export function useGrowthDecline(windowMonths = 12): UseQueryResult<GrowthDeclineResponse> {
  return useQuery({
    queryKey: ['depletions', 'growth-decline', windowMonths],
    queryFn: () =>
      apiGet<GrowthDeclineResponse>('sales', '/api/depletions/growth-decline', {
        window_months: windowMonths,
      }),
  });
}

// ---- product performance (strategic per-SKU view) ----

export type ProductMomentum = 'rising' | 'steady' | 'slipping' | 'declining' | 'new';

// MUST match `schemas/strategic.py::ProductMonthlyVolume`.
export interface ProductMonthlyVolume {
  period: string;
  cases_9l: string;
}

// MUST match `schemas/strategic.py::ProductTopAccount`.
export interface ProductTopAccount {
  account_id: number;
  name: string;
  state_code: string | null;
  premises_type: string | null;
  // True for online-retailer accounts — flagged as an ONLINE channel.
  is_online_retailer: boolean;
  cases_9l: string;
  share: number;
}

// MUST match `schemas/strategic.py::ProductTopState`.
export interface ProductTopState {
  state_code: string | null;
  cases_9l: string;
  share: number;
  account_count: number;
}

// MUST match `schemas/strategic.py::ProductTopDistributor`.
export interface ProductTopDistributor {
  distributor_code: string | null;
  cases_9l: string;
  share: number;
  account_count: number;
}

// MUST match `schemas/strategic.py::ProductPerformanceItem`.
export interface ProductPerformanceItem {
  product_id: number;
  product_name: string;

  cases_9l: string;
  cases_physical: string;
  pct_of_9l: number;

  account_count: number;
  state_count: number;
  avg_9l_per_account: string;

  top_account_id: number | null;
  top_account_name: string | null;
  top_account_share: number;
  top_state: string | null;
  top_state_share: number;

  recent_3m_9l: string;
  prior_3m_9l: string;
  velocity_pct: number | null;
  momentum: ProductMomentum;

  first_active: string | null;
  last_active: string | null;

  monthly_series: ProductMonthlyVolume[];
  top_accounts: ProductTopAccount[];
  top_states: ProductTopState[];

  // Round-2 strategic enrichments.
  recent_12m_9l: string;
  prior_12m_9l: string;
  yoy_pct: number | null;

  top_distributor_code: string | null;
  top_distributor_share: number;
  distributor_count: number;
  top_distributors: ProductTopDistributor[];

  accounts_active_90d: number;
  accounts_gained_90d: number;
  accounts_churned_90d: number;

  states_active_recent_q: number;
  months_active_12m: number;

  peak_month: string | null;
  peak_month_9l: string;
}

export interface ProductPerformanceResponse {
  reference_date: string;
  products: ProductPerformanceItem[];
  total_9l: string;
  top_3_share: number;
}

export function useProductPerformance(
  range?: DateRange,
): UseQueryResult<ProductPerformanceResponse> {
  return useQuery({
    queryKey: ['depletions', 'product-performance', range ?? {}],
    queryFn: () =>
      apiGet<ProductPerformanceResponse>('sales', '/api/depletions/product-performance', {
        from: range?.from,
        to: range?.to,
      }),
  });
}

// ---- state performance (strategic per-state view) ----

export type StateMomentum = ProductMomentum; // same tier scheme

// MUST match `schemas/strategic.py::StateMonthlyVolume`.
export interface StateMonthlyVolume {
  period: string;
  cases_9l: string;
}

// MUST match `schemas/strategic.py::StateYearlyVolume`.
export interface StateYearlyVolume {
  year: number;
  cases_9l: string;
  is_ytd: boolean;
  months_covered: number;
}

// MUST match `schemas/strategic.py::StateTopProduct`.
export interface StateTopProduct {
  product_id: number;
  product_name: string;
  cases_9l: string;
  share: number;
}

// MUST match `schemas/strategic.py::StateTopAccount`.
export interface StateTopAccount {
  account_id: number;
  name: string;
  city: string | null;
  premises_type: string | null;
  // True for online-retailer accounts — flagged as an ONLINE channel.
  is_online_retailer: boolean;
  cases_9l: string;
  share: number;
}

// MUST match `schemas/strategic.py::StateTopDistributor`.
export interface StateTopDistributor {
  distributor_code: string | null;
  cases_9l: string;
  share: number;
  account_count: number;
}

// MUST match `schemas/strategic.py::StatePerformanceItem`.
export interface StatePerformanceItem {
  state_code: string;

  cases_9l: string;
  cases_physical: string;
  pct_of_9l: number;

  account_count: number;
  product_count: number;
  avg_9l_per_account: string;

  top_product_id: number | null;
  top_product_name: string | null;
  top_product_share: number;

  top_account_id: number | null;
  top_account_name: string | null;
  top_account_share: number;

  top_distributor_code: string | null;
  top_distributor_share: number;
  distributor_count: number;

  top_products: StateTopProduct[];
  top_accounts: StateTopAccount[];
  top_distributors: StateTopDistributor[];

  recent_3m_9l: string;
  prior_3m_9l: string;
  velocity_pct: number | null;
  momentum: StateMomentum;

  recent_12m_9l: string;
  prior_12m_9l: string;
  yoy_pct: number | null;

  accounts_active_90d: number;
  accounts_gained_90d: number;
  accounts_churned_90d: number;

  // Per-state ON/OFF/NA/UNKNOWN account counts. Matches
  // `schemas/strategic.py::PremisesCounts`. Sums to `account_count`.
  premises_counts: {
    on: number;
    off: number;
    na: number;
    unknown: number;
  };

  first_active: string | null;
  last_active: string | null;
  months_active_12m: number;

  peak_month: string | null;
  peak_month_9l: string;

  monthly_series: StateMonthlyVolume[];
  yearly_history: StateYearlyVolume[];
}

export interface StatePerformanceResponse {
  reference_date: string;
  states: StatePerformanceItem[];
  total_9l: string;
  top_3_share: number;
}

export function useStatePerformance(range?: DateRange): UseQueryResult<StatePerformanceResponse> {
  return useQuery({
    queryKey: ['depletions', 'state-performance', range ?? {}],
    queryFn: () =>
      apiGet<StatePerformanceResponse>('sales', '/api/depletions/state-performance', {
        from: range?.from,
        to: range?.to,
      }),
  });
}

// ---- account performance (strategic top-N accounts view) ----

export type AccountMomentum = ProductMomentum;

// MUST match `schemas/strategic.py::AccountMonthlyVolume`.
export interface AccountMonthlyVolume {
  period: string;
  cases_9l: string;
}

// MUST match `schemas/strategic.py::AccountTopProduct`.
export interface AccountTopProduct {
  product_id: number;
  product_name: string;
  cases_9l: string;
  share: number;
}

// MUST match `schemas/strategic.py::AccountPerformanceItem`.
export interface AccountPerformanceItem {
  account_id: number;
  name: string;

  address: string | null;
  state_code: string | null;
  city: string | null;
  county: string | null;
  zip_code: string | null;
  distributor_code: string | null;
  premises_type: string | null;
  // True for online-retailer accounts — flagged as an ONLINE channel.
  is_online_retailer: boolean;

  cases_9l: string;
  cases_physical: string;
  pct_of_9l: number;

  product_count: number;
  avg_9l_per_active_month: string;

  top_product_id: number | null;
  top_product_name: string | null;
  top_product_share: number;
  top_products: AccountTopProduct[];

  recent_3m_9l: string;
  prior_3m_9l: string;
  velocity_pct: number | null;
  momentum: AccountMomentum;

  recent_12m_9l: string;
  prior_12m_9l: string;
  yoy_pct: number | null;

  first_active: string | null;
  last_active: string | null;
  months_active_12m: number;

  peak_month: string | null;
  peak_month_9l: string;

  days_since: number | null;

  monthly_series: AccountMonthlyVolume[];
}

export interface AccountPerformanceResponse {
  reference_date: string;
  accounts: AccountPerformanceItem[];
  total_9l: string;
  top_10_share: number;
}

export function useAccountPerformance(
  range?: DateRange,
  limit = 50,
): UseQueryResult<AccountPerformanceResponse> {
  return useQuery({
    queryKey: ['depletions', 'account-performance', range ?? {}, limit],
    queryFn: () =>
      apiGet<AccountPerformanceResponse>('sales', '/api/depletions/account-performance', {
        from: range?.from,
        to: range?.to,
        limit,
      }),
  });
}

// ----------------------------------------------------------------
// Premises Mix
// ----------------------------------------------------------------

// MUST match `schemas/strategic.py::PremisesBucket`.
export interface PremisesBucket {
  premises_type: string | null;
  account_count: number;
  total_9l: string;
  pct_of_9l: number;
}

// MUST match `schemas/strategic.py::PremisesYearlyBucket`.
export interface PremisesYearlyBucket {
  premises_type: string | null;
  year: number;
  cases_9l: string;
  is_ytd: boolean;
  months_covered: number;
}

// MUST match `schemas/strategic.py::OnlineRetailerAccount`.
export interface OnlineRetailerAccount {
  name: string;
  state_code: string | null;
  total_9l: string;
}

// MUST match `schemas/strategic.py::PremisesSummaryResponse`.
export interface PremisesSummaryResponse {
  reference_date: string;
  buckets: PremisesBucket[];
  grand_total_9l: string;
  grand_total_accounts: number;
  yearly_history: PremisesYearlyBucket[];
  // The accounts behind the synthetic 'ONLINE' bucket (highest 9L first).
  online_retailers: OnlineRetailerAccount[];
}

export function useDepletionsPremisesSummary(
  range?: DateRange,
  state?: string | null,
): UseQueryResult<PremisesSummaryResponse> {
  return useQuery({
    queryKey: ['depletions', 'premises-summary', range ?? {}, state ?? 'all'],
    queryFn: () =>
      apiGet<PremisesSummaryResponse>('sales', '/api/depletions/premises-summary', {
        from: range?.from,
        to: range?.to,
        // Omit the param entirely when no state is selected (all states).
        ...(state ? { state } : {}),
      }),
  });
}

// MUST match `schemas/strategic.py::PremisesAccount`.
export interface PremisesAccount {
  account_id: number;
  name: string;
  // Mailing address — included so exports can be printed / mapped.
  address: string | null;
  state_code: string | null;
  city: string | null;
  county: string | null;
  zip_code: string | null;
  premises_type: string;
  raw_premises: string | null;
  total_9l: string;
  cases_physical: string;
  product_count: number;
  // White-space view: catalog products carried vs missing (canonical
  // names, best-selling first).
  carried_products: string[];
  missing_products: string[];
  last_active_period: string | null;
  pct_of_bucket: number;
}

export type PremisesAccountSort = 'volume' | 'name' | 'recent';

export interface PremisesAccountsQuery {
  state?: string | null;
  search?: string;
  sort?: PremisesAccountSort;
  activeOnly?: boolean;
  limit?: number;
}

// MUST match `schemas/strategic.py::PremisesAccountsResponse`.
export interface PremisesAccountsResponse {
  bucket: string;
  accounts: PremisesAccount[];
  total_count: number;
  bucket_total_9l: string;
}

// The accounts behind one premises-mix bucket (drill-down). Only fires
// when `bucket` is set — i.e. a tile has been expanded.
export function useDepletionsPremisesAccounts(
  bucket: string | null,
  opts: PremisesAccountsQuery = {},
): UseQueryResult<PremisesAccountsResponse> {
  const { state, search, sort = 'volume', activeOnly = false, limit = 100 } = opts;
  return useQuery({
    enabled: !!bucket,
    queryKey: [
      'depletions',
      'premises-accounts',
      bucket,
      state ?? 'all',
      search ?? '',
      sort,
      activeOnly,
      limit,
    ],
    queryFn: () =>
      apiGet<PremisesAccountsResponse>('sales', '/api/depletions/premises-accounts', {
        bucket: bucket ?? '',
        sort,
        limit,
        ...(state ? { state } : {}),
        ...(search ? { q: search } : {}),
        ...(activeOnly ? { active_only: 'true' } : {}),
      }),
  });
}

// Plain fetch of a premises bucket's accounts — for exports, which need
// every matching account (backend caps limit at 5000), not the top 100.
export function fetchDepletionsPremisesAccounts(
  bucket: string,
  opts: PremisesAccountsQuery = {},
): Promise<PremisesAccountsResponse> {
  const { state, search, sort = 'volume', activeOnly = false, limit = 5000 } = opts;
  return apiGet<PremisesAccountsResponse>('sales', '/api/depletions/premises-accounts', {
    bucket,
    sort,
    limit,
    ...(state ? { state } : {}),
    ...(search ? { q: search } : {}),
    ...(activeOnly ? { active_only: 'true' } : {}),
  });
}
